import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "popover", "search", "list", "item", "hiddenInput", "triggerLabel"]

  connect() {
    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundKeydown = this.keydown.bind(this)
    this.boundReposition = this.positionPopover.bind(this)
  }

  toggle() {
    if (this.popoverTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.popoverTarget.classList.remove("hidden")
    this.positionPopover()
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundKeydown)
    // Keep the fixed-positioned popover anchored to the trigger while it's open.
    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("scroll", this.boundReposition, true)
  }

  close() {
    this.popoverTarget.classList.add("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.filter()
    }
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
  }

  // Positions the popover using `position: fixed` so it can float beyond
  // overflow-hidden ancestors (e.g. a modal). Anchors to the trigger, matches its
  // width, opens on whichever side has more room, and caps the scrollable list so
  // the popover always fits the viewport instead of overlapping the trigger.
  positionPopover() {
    if (!this.hasTriggerTarget) return

    const popover = this.popoverTarget
    const rect = this.triggerTarget.getBoundingClientRect()
    const gap = 4
    const margin = 8

    popover.style.width = `${rect.width}px`

    // Clear any prior cap so we can measure the popover's natural height.
    if (this.hasListTarget) this.listTarget.style.maxHeight = ""
    const naturalHeight = popover.offsetHeight

    const spaceBelow = window.innerHeight - rect.bottom - gap - margin
    const spaceAbove = rect.top - gap - margin
    const openUpward = spaceBelow < naturalHeight && spaceAbove > spaceBelow
    const available = openUpward ? spaceAbove : spaceBelow

    // If it still won't fit, shrink the scrollable list so it does.
    if (naturalHeight > available && this.hasListTarget) {
      const chrome = naturalHeight - this.listTarget.offsetHeight
      this.listTarget.style.maxHeight = `${Math.max(48, available - chrome)}px`
    }

    const height = popover.offsetHeight
    const top = openUpward ? rect.top - height - gap : rect.bottom + gap

    popover.style.left = `${rect.left}px`
    popover.style.top = `${Math.max(margin, top)}px`
  }

  filter() {
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase() : ""
    this.itemTargets.forEach(item => {
      const text = item.textContent.toLowerCase()
      item.style.display = text.includes(query) ? "" : "none"
    })
  }

  select(event) {
    const value = event.params.value
    const label = event.currentTarget.textContent.trim()

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
    }

    if (this.hasTriggerLabelTarget) {
      this.triggerLabelTarget.textContent = label
    }

    this.close()
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
  }
}
